// Copyright 2026, Auxilio Technologies LLC

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { prepAPI, Order } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasPermission, RoleType, Permissions } from '../config/rolesConfig';
import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';

const PrepQueue: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const role = (user?.role || 'viewer') as RoleType;
  const canWrite = hasPermission(role, Permissions.PREP_WRITE);

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    prepAPI
      .queue()
      .then((res) => setOrders(res.data.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markReady = async (id: string) => {
    setBusy(id);
    try {
      await prepAPI.markReady(id);
      setOrders((prev) => prev.filter((o) => o.id !== id)); // optimistic: drops out of the queue
    } catch {
      load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('prep.title')}</h1>
        <p className="mt-1 text-gray-500 dark:text-gray-400">{t('prep.subtitle')}</p>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
      ) : orders.length === 0 ? (
        <div className="rounded-xl border border-gray-100 bg-white py-12 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <SparklesIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{t('prep.empty')}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('prep.emptyHint')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {orders.map((o) => (
            <div key={o.id} className="flex flex-col rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{o.recipient_name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{o.arrangement_type || '—'}</p>
                </div>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                  {t(`order.slot.${o.delivery_time_slot}`)}
                </span>
              </div>
              {o.special_notes && (
                <p className="mt-3 rounded-lg bg-rose-50 p-2 text-sm text-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                  {o.special_notes}
                </p>
              )}
              {o.card_message && (
                <p className="mt-2 text-sm italic text-gray-500 dark:text-gray-400">“{o.card_message}”</p>
              )}
              {canWrite && (
                <button
                  onClick={() => markReady(o.id)}
                  disabled={busy === o.id}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-3 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  {t('prep.markReady')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrepQueue;
